/**
 * 用户行为跟踪事件类型定义
 */

/**
 * 事件名称枚举
 */
export enum TrackingEvent {
  /** Tab 切换事件 */
  TAB_CLICK = 'tab_click',
  /** 侧边栏点击事件 */
  SIDEBAR_CLICK = 'sidebar_click',
  /** 返回按钮点击事件 */
  BACK_CLICK = 'back_click',
  /** 语言切换事件 */
  LANG_SWITCH = 'lang_switch',
  /** 访问定价页面事件 */
  VISIT_PRICE = 'visit_price',
  /** 访问支付页面事件 */
  VISIT_CHECKOUT = 'visit_checkout',
  /** 创建分享事件（主人侧） */
  SHARE_CREATE = 'share_create',
  /** 打开分享页事件（访客侧） */
  SHARE_VIEW = 'share_view',
  /** 分享页 Get App 按钮点击事件 */
  GET_APP_CLICK = 'get_app_click',
}

/**
 * Tab 名称枚举
 */
export enum TabName {
  /** 总结 Tab */
  SUMMARY = 'summary',
  /** 转录 Tab(旧,保留兼容) */
  TRANSCRIPT = 'transcript',
  /** 翻译 Tab(旧,保留兼容) */
  TRANSLATION = 'translation',
  /** 实时转录 */
  LIVE_TRANSCRIPT = 'live_transcript',
  /** 实时翻译 */
  LIVE_TRANSLATION = 'live_translation',
  /** VOC */
  VOC = 'voc',
  /** 精校转录 */
  FINAL_TRANSCRIPT = 'final_transcript',
  /** 精校翻译 */
  FINAL_TRANSLATION = 'final_translation',
}

/**
 * 菜单项名称枚举
 */
export enum MenuItem {
  /** 历史记录 */
  HISTORY = 'history',
  /** 识别词库 */
  RECOG_DICT = 'recog_dict',
  /** 翻译词库 */
  TRANS_DICT = 'trans_dict',
}

/**
 * Tab 切换事件参数
 */
export interface TabClickParams {
  tab_name: TabName;
}

/**
 * 侧边栏点击事件参数
 */
export interface SidebarClickParams {
  menu_item: MenuItem;
}

/**
 * 返回按钮点击事件参数（无额外参数）
 */
export type BackClickParams = Record<string, never>;

/**
 * 语言切换事件参数
 */
export interface LangSwitchParams {
  /** 目标语言代码，如 'zh-CN', 'en', 'ja' */
  lang_target: string;
}

/**
 * 来源参数（来自分享页等场景的 query 参数透传，用于 GA4 切分）
 */
export interface ShareSourceParams {
  /** 跳转来源标识，如 share-app / share-receiver */
  from?: string;
  /** 来源会议的 roomId */
  source_room_id?: string;
  /** 分享方的 uid */
  source_uid?: string;
}

/**
 * 访问定价页面事件参数
 */
export type VisitPriceParams = ShareSourceParams;

/**
 * 访问支付页面事件参数
 */
export type VisitCheckoutParams = ShareSourceParams;

/**
 * 分享创建事件触发动作枚举
 * - TOGGLE_ON：首次开启实时分享开关
 * - COPY_LINK：复制分享 URL
 * - COPY_QRCODE：复制二维码图片
 * - DOWNLOAD_QRCODE：下载二维码
 */
export type ShareTriggerControl =
  | 'TOGGLE_ON'
  | 'COPY_LINK'
  | 'COPY_QRCODE'
  | 'DOWNLOAD_QRCODE';

/**
 * 分享创建事件参数（主人侧）
 */
export interface ShareCreateParams {
  trigger_control: ShareTriggerControl;
  /** member-center sharelink 链接不含 roomId, 此处可为 null */
  room_id?: string | null;
  /** PRD #4: 会中 URL vs 会后 URL 分流统计 */
  url_type?: 'in_session' | 'post_session';
}

/**
 * 分享访问事件参数（访客侧）
 */
export interface ShareViewParams {
  owner_uid: string;
  room_id: string;
  /** PRD #4: 会中 URL vs 会后 URL 分流统计 */
  url_type?: 'in_session' | 'post_session';
}

/**
 * Get App 按钮点击事件参数（PRD #5）
 */
export interface GetAppClickParams {
  /** 固定为 'share_page'，标识来源 */
  source: string;
  owner_uid: string;
  room_id: string;
}

/**
 * 所有事件参数的联合类型
 */
export type TrackingParams =
  | TabClickParams
  | SidebarClickParams
  | BackClickParams
  | LangSwitchParams
  | VisitPriceParams
  | VisitCheckoutParams
  | ShareCreateParams
  | ShareViewParams
  | GetAppClickParams;
